import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Home, Search, ArrowLeft } from "lucide-react";
import { useLocation, Link } from "wouter";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function NotFound() {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* 404 Content */}
      <section className="flex-1 relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-[oklch(0.15_0.02_210)] via-[oklch(0.18_0.03_220)] to-[oklch(0.20_0.02_210)]">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 left-10 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{
              background: "radial-gradient(circle, oklch(0.65 0.15 230) 0%, transparent 70%)",
              willChange: "transform",
            }}
            animate={{
              x: [0, 50, 0],
              y: [0, 30, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
            style={{
              background: "radial-gradient(circle, oklch(0.58 0.22 345) 0%, transparent 70%)",
              willChange: "transform",
            }}
            animate={{
              x: [0, -30, 0],
              y: [0, 50, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        {/* Content */}
        <div className="container relative z-10 text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-3xl mx-auto"
          >
            {/* 404 Number */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-8"
            >
              <h1 className="text-[150px] md:text-[200px] lg:text-[250px] font-bold leading-none bg-gradient-to-r from-[oklch(0.7_0.15_210)] to-[oklch(0.7_0.15_330)] bg-clip-text text-transparent">
                404
              </h1>
            </motion.div>

            {/* Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Page Not Found
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
                Oops! The page you're looking for seems to have wandered off into the digital void.
                Let's get you back on track.
              </p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Button
                onClick={() => setLocation("/")}
                size="lg"
                className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white px-8 py-6 text-lg"
              >
                <Home className="w-5 h-5 mr-2" />
                Go Home
              </Button>
              
              <Button
                onClick={() => window.history.back()}
                size="lg"
                variant="outline"
                className="px-8 py-6 text-lg border-2"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Go Back
              </Button>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mt-16"
            >
              <p className="text-sm text-muted-foreground mb-4">Or explore these popular pages:</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link href="/services/websites">
                  <a className="text-sm px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-border/50 hover:border-border transition-all">
                    Custom Websites
                  </a>
                </Link>
                <Link href="/services/mobile-apps">
                  <a className="text-sm px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-border/50 hover:border-border transition-all">
                    Mobile Apps
                  </a>
                </Link>
                <Link href="/blog">
                  <a className="text-sm px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-border/50 hover:border-border transition-all">
                    Blog
                  </a>
                </Link>
                <Link href="/case-studies">
                  <a className="text-sm px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-border/50 hover:border-border transition-all">
                    Case Studies
                  </a>
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
